import { ChevronDown, Plus } from "lucide-react";
import { useState } from "react";

import { useToast } from "@/hooks/use-toast";
import { formatCountdown } from "@/hooks/useTestCountdown";
import type { TestAssignment, TestDefinition } from "@/api/tests-api";
import {
  useArchiveDefinition,
  useAssignments,
  useCreateAssignment,
  useDefinitions,
  useDeleteAssignment,
  useDuplicateDefinition,
  usePatchAssignment,
  useResealAssignment,
  useRestoreDefinition,
} from "@/api/tests-api";

import { AppDialog } from "@/components/ui/AppDialog";
import { CandidatePicker, type PickableCandidate } from "./CandidatePicker";
import { CandidatePreviewModal } from "./CandidatePreviewModal";
import { Pagination } from "./Pagination";
import { pageSizeFor } from "@/lib/paging";

/**
 * The exams that exist.
 *
 * This list is the answer to "I created an exam, where is it?" — before
 * it existed a created-but-unassigned exam appeared nowhere except as an
 * option inside the assign dropdown, so there was no way to see its
 * durations, its case counts, or whether anyone had it.
 *
 * An exam is REUSABLE: the only uniqueness constraint is on
 * (exam, candidate), so the same exam goes to as many candidates as the
 * clinic likes. That is why assigning happens here, on the exam itself,
 * rather than only in a separate form — re-assigning to the next
 * candidate is the ordinary act, not an exception.
 */
export function ExamListPanel({
  candidates,
  onEdit,
  onCreate,
}: {
  candidates: { id: string; username: string; full_name: string | null }[];
  /** Opens this exam in the form. */
  onEdit: (exam: TestDefinition) => void;
  /** Opens the form empty, for a new exam. */
  onCreate: () => void;
}) {
  const [showRetired, setShowRetired] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeFor("exams"));
  // Which candidate's exams to show. Filtered on the SERVER, because the
  // list is paged and filtering what happens to be on screen would miss
  // the rest.
  const [candidateFilter, setCandidateFilter] =
    useState<PickableCandidate | null>(null);
  const definitions = useDefinitions({
    includeArchived: showRetired,
    ...(candidateFilter ? { candidateUserId: candidateFilter.id } : {}),
    page,
    pageSize,
  });
  const assignments = useAssignments();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<TestDefinition | null>(null);

  const rows = definitions.data?.items ?? [];
  const total = definitions.data?.total ?? 0;

  return (
    <section data-cy="exam-list">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">
          Exams ({total})
        </h3>
        <div className="flex flex-wrap items-center gap-3">
          <CandidatePicker
            candidates={candidates}
            selected={candidateFilter}
            onSelect={(c) => {
              setPage(1);
              setCandidateFilter(c);
            }}
            onClear={() => {
              setPage(1);
              setCandidateFilter(null);
            }}
          />
          <label className="flex items-center gap-2 text-xs text-foreground-muted">
            <input
              type="checkbox"
              data-cy="toggle-retired-exams"
              checked={showRetired}
              onChange={(e) => {
              // A filter change can leave the current page past the end.
              setPage(1);
              setShowRetired(e.target.checked);
            }}
              className="h-4 w-4 accent-accent"
            />
            Show retired
          </label>
          <button
            type="button"
            data-cy="btn-new-exam"
            onClick={onCreate}
            className="sk-btn sk-btn-primary sk-sm"
          >
            <Plus aria-hidden className="h-3.5 w-3.5" />
            Exam
          </button>
        </div>
      </div>

      {definitions.isLoading ? (
        <p className="text-sm text-foreground-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p data-cy="no-exams" className="text-sm text-foreground-muted">
          {candidateFilter
            ? `${candidateFilter.full_name || candidateFilter.username} has no exams.`
            : "No exams yet — press “+ Exam” to build one."}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((d) => (
            <ExamRow
              key={d.id}
              exam={d}
              assignments={(assignments.data ?? []).filter(
                (a) => a.test_definition_id === d.id,
              )}
              candidates={candidates}
              open={expanded === d.id}
              onToggle={() => setExpanded(expanded === d.id ? null : d.id)}
              onEdit={onEdit}
              onPreview={setPreviewing}
            />
          ))}
        </ul>
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
        label="exams"
      />

      <CandidatePreviewModal
        exam={previewing}
        onClose={() => setPreviewing(null)}
      />
    </section>
  );
}

/**
 * How far a candidate has got, in the words an admin actually uses.
 *
 * The stored statuses are the machine's vocabulary; "attending" and
 * "left early" are what somebody running a hiring round says out loud.
 * The colours are the worklist's own status tokens, which are muted by
 * design — this is a state, not an alarm.
 */
function progressOf(a: TestAssignment): { label: string; tone: string } {
  switch (a.attempt_status) {
    case null:
    case undefined:
      return {
        label: "Not opened",
        tone: "bg-background text-foreground-muted",
      };
    case "NOT_STARTED":
      return {
        label: "Opened, not started",
        tone: "bg-status-new-bg text-status-new",
      };
    case "IN_PROGRESS":
      // The deadline is enforced on the request path, but nothing closes
      // a walked-away attempt until the nightly sweep runs. Saying
      // "Attending · 0:00 left" is the same lie the admin list used to
      // tell; the row reports what is actually true instead.
      return (a.seconds_remaining ?? 1) <= 0
        ? {
            label: "Time up",
            tone: "bg-status-urgent-bg text-status-urgent",
          }
        : { label: "Attending", tone: "bg-status-wip-bg text-status-wip" };
    case "SUBMITTED":
      return { label: "Completed", tone: "bg-status-done-bg text-status-done" };
    case "EXPIRED":
      return {
        label: "Ran out of time",
        tone: "bg-status-urgent-bg text-status-urgent",
      };
    case "ABANDONED":
      return {
        label: "Left early",
        tone: "bg-status-urgent-bg text-status-urgent",
      };
    default:
      return {
        label: a.attempt_status.toLowerCase(),
        tone: "bg-background text-foreground-muted",
      };
  }
}

function ExamRow({
  exam,
  assignments,
  candidates,
  open,
  onToggle,
  onEdit,
  onPreview,
}: {
  exam: TestDefinition;
  assignments: TestAssignment[];
  candidates: { id: string; username: string; full_name: string | null }[];
  open: boolean;
  onToggle: () => void;
  onEdit: (exam: TestDefinition) => void;
  onPreview: (exam: TestDefinition) => void;
}) {
  const { toast } = useToast();
  const createAssignment = useCreateAssignment();
  const deleteAssignment = useDeleteAssignment();
  const duplicate = useDuplicateDefinition();
  const archive = useArchiveDefinition();
  const restore = useRestoreDefinition();
  const reseal = useResealAssignment();
  const patchAssignment = usePatchAssignment();
  const [candidateId, setCandidateId] = useState("");
  // How many sittings the candidate gets. One is the norm; more is for a
  // scheduled re-sit, and granting one AFTER a mishap is the commoner
  // case (see the row action).
  const [attempts, setAttempts] = useState(1);
  const [confirmArchive, setConfirmArchive] = useState(false);
  // Withdraw and re-sit both act on ONE assignment and both are worth a
  // second look, so the pending row is the state and the kind decides
  // what the dialog says. (Archive above keeps its inline confirm: it is
  // already an in-page control, not a browser dialog.)
  const [pending, setPending] = useState<
    { kind: "withdraw" | "resit"; a: TestAssignment } | null
  >(null);

  // Already-assigned candidates are removed from the picker rather than
  // left in to fail: the backend rejects a duplicate with a 409, and an
  // option that can only ever error is not a choice.
  const taken = new Set(assignments.map((a) => a.candidate_user_id));
  const available = candidates.filter((c) => !taken.has(c.id));

  // An exam with no assessment cases cannot be sat. Worth saying on the
  // row, because everything else about it looks finished.
  const unsittable = exam.case_count === 0;
  const retired = exam.status === "ARCHIVED";

  const fail = (err: unknown, fallback: string) => {
    const detail =
      (err as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail ?? fallback;
    toast({ title: detail, variant: "error" });
  };

  const assign = async () => {
    try {
      await createAssignment.mutateAsync({
        test_definition_id: exam.id,
        candidate_user_id: candidateId,
        max_attempts: attempts,
      });
      setCandidateId("");
      setAttempts(1);
      toast({ title: "Assigned" });
    } catch (err) {
      fail(err, "Could not assign this exam");
    }
  };

  const withdraw = async (a: TestAssignment) => {
    try {
      await deleteAssignment.mutateAsync(a.id);
      toast({ title: "Withdrawn" });
    } catch (err) {
      fail(err, "Could not withdraw this exam");
    }
  };

  const update = async (a: TestAssignment) => {
    try {
      await reseal.mutateAsync(a.id);
      toast({ title: `${a.candidate_name} now has the current version` });
    } catch (err) {
      fail(err, "Could not update this candidate");
    }
  };

  const grantResit = async (a: TestAssignment) => {
    const next = a.max_attempts + 1;
    try {
      await patchAssignment.mutateAsync({
        assignmentId: a.id,
        maxAttempts: next,
      });
      toast({ title: `${a.candidate_name} may now sit it ${next} times` });
    } catch (err) {
      fail(err, "Could not grant another attempt");
    }
  };

  const copy = async () => {
    try {
      const made = await duplicate.mutateAsync(exam.id);
      toast({ title: `Copied as “${made.title}”` });
    } catch {
      toast({ title: "Could not copy this exam", variant: "error" });
    }
  };

  const bringBack = async () => {
    try {
      await restore.mutateAsync(exam.id);
      toast({ title: "Restored as a draft" });
    } catch {
      toast({ title: "Could not restore this exam", variant: "error" });
    }
  };

  const retire = async () => {
    try {
      await archive.mutateAsync(exam.id);
      setConfirmArchive(false);
      toast({ title: "Retired" });
    } catch (err) {
      fail(err, "Could not retire this exam");
    }
  };

  return (
    <li
      data-cy="exam-row"
      className="overflow-hidden rounded-lg border border-border bg-background-card"
    >
      {/* The whole header is the control. A row that opens only from a
          small chevron makes people hunt for the hit area, and the
          chevron was the only target here. */}
      <div
        data-cy="btn-exam-expand"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        className="flex w-full cursor-pointer items-start justify-between gap-4 px-4 py-3 text-left hover:bg-table-row-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">
            {exam.title}
            {retired && (
              <span
                data-cy="exam-retired"
                className="ml-2 rounded-full bg-background-subtle px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground-muted"
              >
                Retired
              </span>
            )}
          </p>
          {exam.description && (
            <p className="mt-0.5 truncate text-xs text-foreground-muted">
              {exam.description}
            </p>
          )}
          <p className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-foreground-muted">
            <span data-cy="exam-duration">
              Assessment {formatCountdown(exam.duration_seconds)} ·{" "}
              {exam.case_count} case{exam.case_count === 1 ? "" : "s"}
            </span>
            <span data-cy="exam-practice">
              {exam.dry_run_duration_seconds === null
                ? "No practice run"
                : `Practice ${formatCountdown(exam.dry_run_duration_seconds)} · ${exam.dry_run_case_count} case${
                    exam.dry_run_case_count === 1 ? "" : "s"
                  }`}
            </span>
          </p>
          {unsittable && !retired && (
            <p
              data-cy="exam-unsittable"
              className="mt-1 text-xs text-status-urgent"
            >
              No assessment cases yet — a candidate cannot sit this.
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2 text-xs text-foreground-muted">
          <span>
            {assignments.length === 0
              ? "Not assigned"
              : `${assignments.length} candidate${assignments.length === 1 ? "" : "s"}`}
          </span>
          <ChevronDown
            aria-hidden
            className={[
              "h-4 w-4 transition-transform",
              open ? "rotate-180" : "",
            ].join(" ")}
          />
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background/40 px-4 py-4">
          {/* ---- one toolbar, not buttons scattered down the panel -- */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {!retired && (
              <button
                type="button"
                data-cy="btn-exam-edit"
                onClick={() => onEdit(exam)}
                className="sk-btn sk-btn-primary sk-sm"
              >
                Edit
              </button>
            )}
            <button
              type="button"
              data-cy="btn-exam-preview"
              onClick={() => onPreview(exam)}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-table-row-hover"
            >
              View as candidate
            </button>
            <button
              type="button"
              data-cy="btn-exam-duplicate"
              onClick={() => {
                void copy();
              }}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-table-row-hover"
            >
              Duplicate
            </button>

            {/* Destructive lives at the far end, away from the rest. */}
            <span className="ml-auto flex items-center gap-2">
              {retired ? (
                <button
                  type="button"
                  data-cy="btn-exam-restore"
                  onClick={() => {
                    void bringBack();
                  }}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-table-row-hover"
                >
                  Restore
                </button>
              ) : confirmArchive ? (
                <>
                  <span className="text-xs text-foreground-muted">
                    Results are kept; it stops being offered.
                  </span>
                  <button
                    type="button"
                    data-cy="btn-exam-archive-confirm"
                    onClick={() => {
                      void retire();
                    }}
                    className="rounded-md bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground"
                  >
                    Retire
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmArchive(false)}
                    className="text-xs font-semibold text-foreground-muted hover:underline"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  data-cy="btn-exam-archive"
                  onClick={() => setConfirmArchive(true)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-destructive hover:bg-table-row-hover"
                >
                  Retire
                </button>
              )}
            </span>
          </div>

          {/* ---- who has it, and where they have got to ------------ */}
          {assignments.length > 0 && (
            <div className="mb-4 overflow-x-auto rounded-md border border-border/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-subtle/60 text-left text-[11px] uppercase tracking-wide text-foreground-muted">
                    <th className="px-3 py-2 font-medium">Candidate</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Time used</th>
                    <th className="px-3 py-2 font-medium">Attempts</th>
                    <th className="px-3 py-2 font-medium">Version</th>
                    <th className="px-3 py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => {
                    const p = progressOf(a);
                    return (
                      <tr
                        key={a.id}
                        data-cy="exam-assignee"
                        className="border-t border-border/50 odd:bg-table-row-alt/40"
                      >
                        <td className="px-3 py-2 text-foreground">
                          {a.candidate_name}
                          <span className="ml-2 text-xs text-foreground-muted">
                            {a.sealed_case_count} case
                            {a.sealed_case_count === 1 ? "" : "s"}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            data-cy="assignee-status"
                            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${p.tone}`}
                          >
                            {p.label}
                          </span>
                        </td>
                        {/* Time SPENT once they are done; time LEFT while
                            they are still in it, which is the number that
                            matters at that moment. */}
                        <td className="px-3 py-2 tabular-nums text-xs text-foreground-muted">
                          {a.attempt_status === "IN_PROGRESS"
                            ? a.seconds_remaining != null &&
                              a.seconds_remaining > 0
                              ? `${formatCountdown(a.seconds_remaining)} left`
                              : "—"
                            : a.time_utilised_seconds != null
                              ? formatCountdown(a.time_utilised_seconds)
                              : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-foreground-muted">
                          <span className="tabular-nums">
                            {a.attempts_used} / {a.max_attempts}
                          </span>
                          {/* Offered once they have used everything they
                              have — before that there is nothing to grant,
                              and the button would only confuse. */}
                          {a.attempts_used >= a.max_attempts && (
                            <button
                              type="button"
                              data-cy="btn-grant-resit"
                              onClick={() => {
                                setPending({ kind: "resit", a });
                              }}
                              className="ml-2 rounded-md border border-status-wip/50 bg-status-wip-bg px-2 py-0.5 text-[11px] text-status-wip hover:bg-status-wip/20"
                            >
                              + Re-sit
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {a.matches_current_exam ? (
                            <span className="text-foreground-muted">
                              Current
                            </span>
                          ) : (
                            <span
                              data-cy="assignee-stale"
                              className="text-status-urgent"
                            >
                              {a.has_begun
                                ? "Sat an earlier one"
                                : "Earlier version"}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          {/* Same bordered pills as Staff accounts. */}
                          <div className="flex justify-end gap-2">
                            {!a.matches_current_exam && !a.has_begun && (
                              <button
                                type="button"
                                data-cy="btn-reseal"
                                onClick={() => {
                                  void update(a);
                                }}
                                className="sk-btn sk-sm"
                              >
                                Update
                              </button>
                            )}
                            {a.has_begun ? (
                              <span
                                className="rounded-md border border-border px-2 py-1 text-xs text-foreground-muted"
                                title="Their paper is sealed once they begin"
                              >
                                Sealed
                              </span>
                            ) : (
                              <button
                                type="button"
                                data-cy="btn-withdraw"
                                onClick={() => {
                                  setPending({ kind: "withdraw", a });
                                }}
                                className="rounded-md border border-status-urgent/50 bg-status-urgent-bg px-2 py-1 text-xs text-status-urgent hover:bg-status-urgent/20"
                              >
                                Withdraw
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ---- assign to somebody new ---------------------------- */}
          {retired ? (
            <p className="text-xs text-foreground-muted">
              Retired exams cannot be assigned. Existing candidates keep theirs.
            </p>
          ) : available.length === 0 ? (
            <p className="text-xs text-foreground-muted">
              {candidates.length === 0
                ? "No candidate accounts yet. Create one under Create user with the Test Candidate role."
                : "Every candidate already has this exam."}
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <label className="sk-field flex flex-col">
                <span className="sk-label">Assign to</span>
                <select
                  data-cy="exam-assign-candidate"
                  value={candidateId}
                  onChange={(e) => setCandidateId(e.target.value)}
                  className="sk-input mt-1 w-56"
                >
                  <option value="">Select…</option>
                  {available.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name || c.username}
                    </option>
                  ))}
                </select>
              </label>
              <label className="sk-field flex flex-col">
                <span className="sk-label">Attempts</span>
                <input
                  type="number"
                  data-cy="exam-assign-attempts"
                  min={1}
                  max={10}
                  value={attempts}
                  onChange={(e) =>
                    setAttempts(
                      Math.min(10, Math.max(1, Number(e.target.value) || 1)),
                    )
                  }
                  className="sk-input mt-1 w-20"
                />
              </label>
              <button
                type="button"
                data-cy="btn-exam-assign"
                disabled={!candidateId}
                onClick={() => {
                  void assign();
                }}
                className="sk-btn sk-btn-primary"
              >
                Assign
              </button>
            </div>
          )}

          <p className="mt-4 text-[11px] text-foreground-muted">
            Editing this exam affects candidates assigned from then on. Anyone
            who already has it keeps the version they were given — use Update
            to hand a candidate the current one, which is possible until they
            start.
          </p>
        </div>
      )}

      {/* Withdraw and re-sit, confirmed in the app rather than by the
          browser. Both change what a candidate can do next, and one of
          them destroys an assignment. */}
      <AppDialog
        open={pending?.kind === "withdraw"}
        cy="dialog-withdraw"
        tone="danger"
        title="Withdraw this exam?"
        body={
          pending
            ? `${pending.a.candidate_name ?? "This candidate"} loses access to it. Anything they have already recorded stays, and you can assign it again afterwards — but they would be given the exam as it stands today, not the paper they had.`
            : undefined
        }
        onDismiss={() => setPending(null)}
        actions={[
          {
            label: "Withdraw it",
            cy: "btn-confirm-withdraw",
            variant: "danger",
            onClick: () => {
              const a = pending?.a;
              setPending(null);
              if (a) void withdraw(a);
            },
          },
          {
            label: "Keep it assigned",
            cy: "btn-cancel-withdraw",
            variant: "ghost",
            autoFocus: true,
            onClick: () => setPending(null),
          },
        ]}
      />

      <AppDialog
        open={pending?.kind === "resit"}
        cy="dialog-resit"
        tone="info"
        title="Give another attempt?"
        body={
          pending
            ? `${pending.a.candidate_name ?? "This candidate"} would be allowed ${pending.a.max_attempts + 1} sittings instead of ${pending.a.max_attempts}. They sit THE SAME PAPER they were given, not the current version of the exam — the seal does not move.`
            : undefined
        }
        onDismiss={() => setPending(null)}
        actions={[
          {
            label: "Grant a re-sit",
            cy: "btn-confirm-resit",
            variant: "primary",
            autoFocus: true,
            onClick: () => {
              const a = pending?.a;
              setPending(null);
              if (a) void grantResit(a);
            },
          },
          {
            label: "Cancel",
            cy: "btn-cancel-resit",
            variant: "ghost",
            onClick: () => setPending(null),
          },
        ]}
      />
    </li>
  );
}
